// CollaborationsPage.jsx
// Updated: centered content + custom button colors + fully rounded buttons + circular chips

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Button,
  Paper,
  useTheme,
} from "@mui/material";

import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const partnerships = [
  {
    title: "Hospitals & Healthcare Institutions",
    icon: <LocalHospitalOutlinedIcon />,
    description:
      "Working together to support clinical research, healthcare innovation, and improved patient outcomes.",
    tags: ["Clinical Research", "Patient Outcomes", "Healthcare Innovation"],
  },
  {
    title: "Academic Institutions",
    icon: <SchoolOutlinedIcon />,
    description:
      "Partnering with universities and colleges to advance education, research, and knowledge exchange.",
    tags: ["Universities", "Research", "Knowledge Exchange"],
  },
  {
    title: "Incubators & Innovation Hubs",
    icon: <HubOutlinedIcon />,
    description:
      "Supporting startups and entrepreneurs through mentorship, resources, and collaborative development.",
    tags: ["Startups", "Mentorship", "Innovation"],
  },
  {
    title: "Government Research Bodies",
    icon: <AccountBalanceOutlinedIcon />,
    description:
      "Engaging with public research organizations to contribute to policy-driven research, national initiatives, and societal development.",
    tags: ["Public Research", "National Initiatives", "Policy"],
  },
];

function PartnershipCard({ item }) {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 5,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
        textAlign: "center",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 18px 45px rgba(0,0,0,0.10)",
          borderColor: theme.palette.primary.light,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 220px at 50% -10%, rgba(25,118,210,0.16), transparent 55%)",
          pointerEvents: "none",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ color: "primary.main", display: "grid", placeItems: "center" }}>
              {item.icon}
            </Box>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {item.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Partnerships that drive impact
          </Typography>
        </Stack>

        <Typography variant="body1" sx={{ mt: 2, color: "text.primary" }}>
          {item.description}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          {item.tags.map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 700 }}
            />
          ))}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Button
          size="small"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 999,
            px: 2,
            color: "#6A1B9A",
            "&:hover": { bgcolor: "rgba(106,27,154,0.08)" },
          }}
        >
          Learn more
        </Button>
      </CardContent>
    </Card>
  );
}

export default function CollaborationsPage() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, sm: 9, md: 10 },
          borderBottom: "1px solid",
          borderColor: "divider",
          background:
            "radial-gradient(1200px 500px at 20% 0%, rgba(25,118,210,0.18), transparent 60%), radial-gradient(900px 420px at 90% 15%, rgba(156,39,176,0.12), transparent 55%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Chip
                  icon={<HandshakeOutlinedIcon />}
                  label="Collaborations & Partnerships"
                  variant="outlined"
                  sx={{
                    fontWeight: 800,
                    borderRadius: 999,
                    px: 1,
                    bgcolor: "rgba(255,255,255,0.7)",
                    backdropFilter: "blur(10px)",
                  }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: "-0.02em",
                    fontSize: { xs: "2.05rem", sm: "2.6rem", md: "3.1rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Building impact through strong partnerships
                </Typography>

                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ fontWeight: 500, maxWidth: 720, lineHeight: 1.6 }}
                >
                  We collaborate with a wide range of organizations to strengthen innovation,
                  research, and real-world impact across healthcare, academia, startups, and
                  government research ecosystems.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      borderRadius: 999,
                      px: 3.5,
                      py: 1.2,
                      bgcolor: "#0F766E", // teal
                      "&:hover": { bgcolor: "#0B5F59" },
                    }}
                  >
                    Partner with us
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      borderRadius: 999,
                      px: 3.5,
                      py: 1.2,
                      borderColor: "#0F766E",
                      color: "#0F766E",
                      "&:hover": { borderColor: "#0B5F59", bgcolor: "rgba(15,118,110,0.08)" },
                    }}
                  >
                    View collaboration areas
                  </Button>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 1, md: 2 },
                  p: { xs: 2.5, sm: 3.5 },
                  borderRadius: 5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "rgba(255,255,255,0.75)",
                  backdropFilter: "blur(10px)",
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 950 }}>
                  Our collaboration focus
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 820, mx: "auto" }}>
                  Through these collaborations, we aim to foster knowledge sharing, innovation, and
                  sustainable growth across industries.
                </Typography>

                <Divider sx={{ my: 2.5 }} />

                <Stack spacing={1.25} alignItems="center">
                  {[
                    "Clinical & applied research enablement",
                    "Education partnerships & capacity building",
                    "Startup incubation and technology translation",
                    "Policy-aligned research & national initiatives",
                  ].map((x) => (
                    <Stack
                      key={x}
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "#0F766E",
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {x}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CARDS */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, sm: 6, md: 7 } }}>
        <Stack spacing={1} sx={{ mb: { xs: 2.5, sm: 3 } }} alignItems="center" textAlign="center">
          <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: "-0.01em" }}>
            Partnership categories
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 820 }}>
            Our partnerships span across multiple sectors, including hospitals, academic
            institutions, incubators, and government research bodies.
          </Typography>
        </Stack>

        <Grid container spacing={2.5} justifyContent="center">
          {partnerships.map((item) => (
            <Grid key={item.title} item xs={12} sm={6} md={6}>
              <PartnershipCard item={item} />
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Box sx={{ mt: { xs: 4, sm: 5 }, textAlign: "center" }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 5,
              border: "1px solid",
              borderColor: "divider",
              background:
                "radial-gradient(900px 260px at 50% -20%, rgba(15,118,110,0.18), transparent 55%)",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 950 }}>
              Let’s collaborate
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 760, mx: "auto" }}
            >
              If you represent a hospital, academic institution, incubator, or government research
              body, we’d love to explore ways to work together.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 2.5 }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  textTransform: "none",
                  fontWeight: 950,
                  borderRadius: 999,
                  px: 3.5,
                  bgcolor: "#6A1B9A", // purple
                  "&:hover": { bgcolor: "#4A148C" },
                }}
              >
                Contact us
              </Button>

              <Button
                variant="outlined"
                size="large"
                sx={{
                  textTransform: "none",
                  fontWeight: 950,
                  borderRadius: 999,
                  px: 3.5,
                  borderColor: "#6A1B9A",
                  color: "#6A1B9A",
                  "&:hover": { borderColor: "#4A148C", bgcolor: "rgba(106,27,154,0.08)" },
                }}
              >
                Download partnership deck
              </Button>
            </Stack>
          </Paper>
        </Box>

        
      </Container>
    </Box>
  );
}
